import * as E from "@numi/engine";
import type { Question } from "@numi/engine";
import { S, touch, go, prog, saveProg, queue, addStars, logDoubt, resolveDoubts, autoRead, kid, pushRecent, comfort, type QState } from "./store.ts";
import { SFX, speak } from "./sound.ts";
import { tutor } from "./api.ts";
import { burstAt } from "./ui.tsx";

export const ROUND = 5, SHORT_ROUND = 3, REVIEW_AT = 3;
/** Questions in a new topic round: 5, or 3 when a grown-up turned on shorter rounds. */
export const roundSize = () => (comfort().shortRounds ? SHORT_ROUND : ROUND);
export const roundLen = () => S.round?.len ?? roundSize();
/** Learning comfort: a stretch break after about 9 minutes of active play. */
export const BREAK_AFTER_SECS = 9 * 60;
import { completeRunningStep, savePlan } from "./plan.ts";
export const sayAloud = (t: string, native = false) => { if (autoRead()) speak(E.plain(t), S.child?.board, { native }); };
const later = (f: () => void) => requestAnimationFrame(() => requestAnimationFrame(f));

function newQS(q: Question, ctx: "try" | "play", intro: string): QState {
  return { ctx, q, typed: "", tries: 0, done: false, revealed: false, feedback: { cls: "", text: intro }, stuck: null, aiBubble: null, story: null, doubtIds: [], t0: Date.now(), hints: 0 };
}
export function startLearn(fromPlan = false) {
  S.step = 0; S.qs = null;
  // If not launched from the plan (e.g. "Learn again" from Done), clear planRunning
  // so a subsequent finishRound() doesn't tick the wrong plan step.
  if (!fromPlan) S.planRunning = null;
  go("learn");
}
export function startTry() {
  const tid = S.topic!; const q = E.makeBuild(tid, 1) || E.topic(tid)!.gen(1); q.tid = tid;
  S.qs = newQS(q, "try", `Your turn, ${E.esc(kid())}. Try this one.`); touch();
}
export function startRound() { S.round = { n: 0, right: 0, marks: [], len: roundSize() }; S.result = null; nextRoundQ(); go("play"); }
/** A short round made of chosen questions (warm-up, stretch). */
export function startQueueRound(items: { tid: string; level: 1 | 2 | 3; label?: string }[]) { S.round = { n: 0, right: 0, marks: [], queue: items, len: items.length }; S.result = null; nextRoundQ(); go("play"); }

function pickReview(): { tid: string; level: 1 | 2 | 3; label: string } | null {
  const grade = S.child!.grade;
  if (S.topic === "abacus") return null;
  const others = E.topicsFor(grade).filter((t) => t.id !== S.topic && S.progress[t.id]?.seen > 0);
  if (others.length) {
    others.sort((a, b) => (S.progress[a.id].last || 0) - (S.progress[b.id].last || 0));
    const t = others[0]; return { tid: t.id, level: S.progress[t.id].level, label: `Quick review: ${E.topicTitle(t)}` };
  }
  if (grade > 1) { const t = E.pick(E.topicsFor(grade - 1)); return { tid: t.id, level: 3, label: `Warm-up from ${E.board().gradeWord} ${grade - 1}` }; }
  return null;
}
/** Draws a question that has not already been asked this round (a few attempts, then accepts a repeat). */
function fresh(R: NonNullable<typeof S.round>, gen: () => Question): Question {
  let q = gen();
  for (let i = 0; i < 6 && R.seen?.includes(E.plain(q.text)); i++) q = gen();
  (R.seen ||= []).push(E.plain(q.text));
  return q;
}
export function nextRoundQ() {
  const R = S.round!;
  if (R.queue) { const it = R.queue[R.n]; const q = fresh(R, () => E.nextQuestion(it.tid, it.level)); q.tid = it.tid; (q as any).review = it.label ?? null;
    S.easier = false; S.qs = newQS(q, "play", `Question ${R.n + 1} of ${R.len}. ${it.label ? E.esc(it.label) + "." : ""}`); if (S.storyMode) makeStory(); return; }
  const len = R.len ?? ROUND;
  const rv = R.n === REVIEW_AT && len > REVIEW_AT + 1 ? pickReview() : null;
  // A question the child missed earlier in this round comes back as the last one, so "we'll try another like it" is true.
  const again = !rv && R.n === len - 1 && R.retry?.length ? R.retry.shift()! : null;
  const tid = rv ? rv.tid : again || S.topic!;
  const q = fresh(R, () => rv ? E.topic(tid)!.gen(rv.level) : E.nextQuestion(tid, prog(tid).level));
  q.tid = tid; (q as any).review = rv?.label ?? null;
  const wasTry = (S as any).fromTry; if (wasTry) (S as any).fromTry = false;
  const plan = comfort().preview && R.n === 0 ? `Here's the plan: ${len} questions, then you see your stars. ` : comfort().preview && R.n === len - 1 ? "This is the last question. " : "";
  const intro = plan + (S.easier && (again || rv) ? "Let's slow down a little. " : "") + (again ? "One more like the one you missed. You can do this." : rv ? `${E.esc(rv.label)}. Let's see if you remember this one.` : wasTry ? `Great try! Now let's practise — question 1 of ${len}.` : S.easier ? "Let's try one that's a bit easier, then build back up." : `Question ${R.n + 1} of ${len}. Take your time, ${E.esc(kid())}.`);
  S.easier = false; S.qs = newQS(q, "play", intro);
  if (S.storyMode) makeStory();
}
export function nextQ() {
  const qs = S.qs!;
  if (qs.ctx === "try") {
    // If the "try" question was the tail of a plan's learn step, mark that step done
    // before the round begins so the plan card shows the learn step ticked.
    if (S.planRunning != null && S.plan) {
      const st = S.plan.steps[S.planRunning];
      if (st?.kind === "learn") { st.done = true; S.planJustDone = S.planRunning; S.planRunning = null; savePlan(); }
    }
    (S as any).fromTry = true; startRound(); return;
  }
  const resp = pace(qs);
  if (S.round!.n >= roundLen()) { finishRound(); maybeBreak(resp); return; }
  if (resp === "easier") easeDown(qs.q.tid as string);
  nextRoundQ();
  if (resp === "picture" && S.qs && E.topic(S.qs.q.tid as string)?.lesson?.length) { (S.qs as any).offer = "picture"; S.qs.feedback = { cls: "", text: `You're working hard, ${E.esc(kid())}. Shall we look at the picture steps together first?` }; }
  maybeBreak(resp);
  touch(); window.scrollTo({ top: 0, behavior: "smooth" });
}

/* ---------- pacing: notice a struggle and respond gently (for every child) ---------- */
/** Records how the finished question went and returns Numi's response, if any. */
function pace(qs: QState) {
  return E.paceAfter(S.pace, { firstTry: qs.tries === 0 && !qs.revealed, tries: qs.tries, hints: qs.hints || 0, ms: (qs.doneAt || Date.now()) - (qs.t0 || Date.now()) });
}
/** One level easier on this topic (never below level 1), so the next questions rebuild confidence. */
function easeDown(tid: string) {
  const p = prog(tid); if (p.level > 1) { p.level = (p.level - 1) as 1 | 2 | 3; p.miss = 0; p.streak = 0; saveProg(tid); }
  S.easier = true;
}
/** A stretch break: when pacing asks for one, or after ~9 active minutes if a grown-up turned breaks on. */
function maybeBreak(resp: string | null) {
  if (resp === "break") S.breakOpen = "pace";
  else if (comfort().breaks && S.sinceBreak >= BREAK_AFTER_SECS) S.breakOpen = "timer";
}
/** "Show me the steps": the topic's picture lesson, then straight back to practice. */
export function showPictureSteps() {
  const tid = S.qs?.q.tid as string | undefined; if (!tid) return;
  S.topic = tid; S.round = null; S.qs = null; S.step = 0; go("learn");
}
export function setFb(cls: "" | "good" | "warn", text: string) { S.qs!.feedback = { cls, text }; touch(); }

export function press(k: string) {
  const qs = S.qs!; if (qs.done) { if (k === "ok") nextQ(); return; }
  if (k === "ok") { if (qs.typed) check(qs.typed); return; }
  SFX.tap();
  if (k === "⌫") qs.typed = qs.typed.slice(0, -1);
  else if (k === ".") { if (!qs.typed.includes(".")) qs.typed = (qs.typed || "0") + "."; }
  else if (qs.typed.length < 9) qs.typed = (qs.typed === "0" ? "" : qs.typed) + k;
  (qs as any).wrong = false; touch();
}

export function checkBuild() {
  const qs = S.qs!, q = qs.q, s = q.st; if (qs.done) return;
  if ((q.build === "frame" || q.build === "shade") && !s.on.length) { setFb("warn", "Tap the parts first, then press Check."); return; }
  if (q.build === "blocks" && !(s.h || s.t || s.o)) { setFb("warn", "Tap + to add some blocks first."); return; }
  if (q.build === "share") {
    if (s.pile) { setFb("warn", `Keep sharing! ${s.pile} sweet${s.pile === 1 ? "" : "s"} still in the pile.`); SFX.tap(); return; }
    if (s.plates.some((c: number) => c !== s.plates[0])) { check("__unfair"); return; }
  }
  check(E.buildValue(q) as any);
}

function solveBuild(q: Question) {
  const s = q.st, a = Number(q.answer);
  if (q.build === "frame" || q.build === "shade") s.on = Array.from({ length: a }, (_, i) => i);
  if (q.build === "blocks") { s.h = Math.floor(a / 100); s.t = Math.floor((a % 100) / 10); s.o = a % 10; }
}

export function check(v: string | number, btnId?: string) {
  const qs = S.qs!, q = qs.q, tid = q.tid as string, p = prog(tid), R = qs.ctx === "play" ? S.round : null;
  const ok = E.isCorrect(q, v);
  if (ok) {
    qs.done = true; qs.doneAt = Date.now(); const first = qs.tries === 0; let up = false;
    if (R) {
      p.seen++; p.last = Date.now(); R.marks[R.n] = first ? "ok" : "meh"; R.n++;
      if (first) { p.right++; p.stars++; p.streak++; p.miss = 0; R.right++; } else p.streak = 0;
      pushRecent(p, first); R.combo = first ? (R.combo || 0) + 1 : 0;
      if (tid === S.topic && p.streak >= 3 && p.level < 3) { p.level = (p.level + 1) as 1 | 2 | 3; p.streak = 0; up = true; }
      saveProg(tid); queue({ type: "attempt", topicId: tid, kind: "practice", correct: true, firstTry: first, level: p.level });
    }
    resolveDoubts(qs.doubtIds); qs.stuck = null;
    const tip = first ? E.smartTip(tid, q, S.child!.grade) : null;
    const combo = R ? R.combo || 0 : 0;
    const msg = E.praise(q, first) + (combo >= 2 && first ? ` <b>${combo} in a row!</b>` : "") + (up ? ` <b>Level up: now Level ${p.level}.</b>` : "") + (tip ? ` <br/><span class="smarttip">💡 ${E.esc(tip.text)}</span>` : "");
    qs.feedback = { cls: "good", text: msg }; (qs as any).mood = "wow cheer";
    if (first) addStars(1); else touch();
    up ? SFX.up() : combo >= 2 && first ? SFX.combo(combo) : SFX.ok();
    if (first) later(() => { burstAt(document.getElementById("disp") || document.getElementById(btnId || "") || document.getElementById("build") || document.getElementById("choices"), Math.min(16 + combo * 4, 40)); SFX.star(); });
    sayAloud(msg); return;
  }
  qs.tries++; SFX.no();
  const diag = E.diagnose(q, v);
  if (R && qs.tries === 1) {
    p.miss++; p.streak = 0; p.last = Date.now();
    if (tid === S.topic && p.miss >= 2 && p.level > 1) { p.level = (p.level - 1) as 1 | 2 | 3; p.miss = 0; S.easier = true; }
    saveProg(tid); queue({ type: "attempt", topicId: tid, kind: "practice", correct: false, firstTry: false, level: p.level, mistake: diag });
  }
  if (btnId) ((qs as any).wrongChoices ||= []).push(String(v));
  (qs as any).mood = "think";
  if (qs.tries >= 3) {
    qs.done = true; qs.revealed = true; qs.stuck = null; qs.doneAt = Date.now();
    if (R) { p.seen++; pushRecent(p, false); R.marks[R.n] = "meh"; R.n++; saveProg(tid); }
    if (q.type === "build" && q.build !== "share" && q.build !== "clock") solveBuild(q);
    // Only promise a repeat if there is a slot left in this round to serve it.
    const again = !!R && !R.queue && R.n < (R.len ?? ROUND);
    if (again) (R!.retry ||= []).push(tid);
    qs.feedback = { cls: "warn", text: `Let's look at it together. ${E.esc(q.explain)}${again ? " We'll try another one like it before the end." : ""}` }; touch(); sayAloud(qs.feedback.text); return;
  }
  const msg = `${E.closeness(q, v)} ${E.esc(diag || q.hints[Math.min(qs.tries - 1, q.hints.length - 1)])}`;
  qs.feedback = { cls: "warn", text: msg }; (qs as any).wrong = q.type === "num"; qs.typed = "";
  touch(); sayAloud(msg);
}

export function finishRound() {
  const R = S.round!; let prev: number | null = null;
  if (!R.queue) { const p = prog(S.topic!); prev = p.lastRound ?? null; p.lastRound = R.right; saveProg(S.topic!); }
  S.rounds++;
  S.result = { kind: "round", right: R.right, of: roundLen(), prev, sticker: S.pendingSticker, mixed: !!R.queue, brk: S.rounds % 3 === 0 };
  completeRunningStep();
  S.pendingSticker = null; go("done");
}

/* ---------- AI helpers ---------- */
const qCtx = (q: Question) => ({ text: E.plain(q.say || q.text), answer: q.answer, tid: q.tid as string });
export async function aiExplain() {
  const qs = S.qs!, q = qs.q; qs.aiBubble = "Thinking..."; touch();
  try { const text = await tutor(S.child!.id, { mode: "explain", question: qCtx(q), topicId: q.tid as string }, (t) => { if (S.qs === qs) { qs.aiBubble = t; touch(); } }); qs.aiBubble = text; sayAloud(text, true); }
  catch (e: any) { qs.aiBubble = e.message || "Numi couldn't answer just now."; }
  touch();
}
const STORY_CAP = 10; // max AI story calls per session (BUG-013)
export async function makeStory() {
  const qs = S.qs!, q = qs.q; if (!q.seed || !q.nums) return;
  if (S.storyCallsThisSession >= STORY_CAP) return; // quota guard
  S.storyCallsThisSession++;
  try {
    const raw = await tutor(S.child!.id, { mode: "story", seed: q.seed, nums: q.nums });
    const m = raw.match(/\{[\s\S]*\}/); const story = m ? String(JSON.parse(m[0]).story || "") : "";
    if (S.qs === qs && story && q.nums.every((n) => story.includes(String(n)))) { qs.story = story; touch(); }
  } catch {}
}

/* ---------- I'm stuck ---------- */
export function openStuckMenu() {
  S.qs!.stuck = { kind: null, msgs: [], turns: [], busy: false, answered: false }; SFX.tap(); touch();
  sayAloud("What's tricky? " + Object.values(E.STUCK).map((v) => v.label).join(". ") + ".");
}
export function closeStuck() { S.qs!.stuck = null; touch(); }
const stuckSay = (t: string, me = false) => S.qs!.stuck!.msgs.push({ t, me });
export function openStuck(kind: "q" | "start" | "diff") {
  const qs = S.qs!, q = qs.q; SFX.tap();
  qs.stuck = { kind, msgs: [], turns: [], busy: false, answered: false };
  qs.doubtIds.push(logDoubt(kind, E.plain(q.say || q.text), q.tid as string));
  if (kind === "q") {
    stuckSay(`Let's read it slowly together. ${E.plain(q.say || q.text)}`);
    stuckSay((q.viz || q.type === "build" ? "Now look at the picture. " : "") + q.hints[0]);
    touch(); sayAloud(qs.stuck.msgs.map((m) => m.t).join(" "));
    aiTurn("rephrase"); return;
  }
  if (kind === "start") { touch(); aiTurn("stuck", "I don't know how to start.", q.hints[0]); return; }
  stuckSay(q.type === "choice" ? "Which answer did you pick? Type it here." : q.build ? "Build your answer, then press Check. I'll help you find what to fix." : "What answer did you get? Type it here.");
  if (q.build) qs.stuck.answered = true;
  touch(); sayAloud(qs.stuck.msgs[0].t);
}
export function stuckReply(v: string) {
  const qs = S.qs!, st = qs.stuck!, q = qs.q; stuckSay(v, true);
  if (st.kind === "diff" && !st.answered) {
    st.answered = true;
    if (E.isCorrect(q, v.trim())) { stuckSay("That's right. Put it in the answer box and press Check."); touch(); sayAloud("That's right. Put it in the answer box."); return; }
    const d = E.diagnose(q, v);
    aiTurn("stuck", `I got ${v}.` + (d ? ` (Teacher's note: likely mistake: ${d})` : ""), d || `${E.closeness(q, v)} ${q.hints[0]}`); return;
  }
  aiTurn("stuck", v, "Try the next small step, then put your answer in the box.");
}
/** One Claude turn inside the stuck panel. Falls back to the built-in hint if Numi can't be reached. */
async function aiTurn(mode: "stuck" | "rephrase", userText?: string, fallback?: string) {
  const qs = S.qs!, st = qs.stuck!, q = qs.q;
  if (userText) st.turns.push({ role: "user", content: userText });
  st.busy = true; const m = { t: "Thinking...", me: false }; st.msgs.push(m); touch();
  try {
    const text = await tutor(S.child!.id, { mode, stuckKind: st.kind || "start", question: qCtx(q), topicId: q.tid as string, messages: st.turns.slice(-12) },
      (t) => { if (S.qs === qs) { m.t = t; touch(); } });
    m.t = text; st.turns.push({ role: "assistant", content: text }); sayAloud(text, true);
  } catch (e: any) {
    if (fallback) { m.t = fallback; sayAloud(fallback); } else st.msgs.pop();
    if (userText) st.turns.pop();
  }
  st.busy = false; touch();
}
